import { type CalenderAppointmentsSearchFilters } from "@/features/calendar/calendarFilter/appointmentFilterReducer";
import { selectEmail } from "@/features/calendar/calendarFilter/appointmentFilterSelector";
import {
  getFirstDateOfWeek,
  getLastDateOfWeek,
} from "@/features/calendar/utils/dateUtils";
import { type Appointment, type Task, fetcher } from "@repo/api";
import { formatToIso, isDefined } from "@repo/utils";
import { endOfDay, startOfDay } from "date-fns";
import useSWR from "swr";

import { type CoWorkerScheduleActivity, type OutlookEvent } from "./types";

export const isOutlookEvent = (
  data: OutlookEvent | Task | Appointment | CoWorkerScheduleActivity
): data is OutlookEvent => {
  return (
    (data as OutlookEvent)?.subject !== undefined &&
    (data as OutlookEvent)?.showAs !== undefined
  );
};

export const useUserCalendarEvents = (
  filters: CalenderAppointmentsSearchFilters
) => {
  const showWeek = filters.tableView === "OVERVIEW";

  const emailIds = filters.calendarSettings.showOutlookCalendar
    ? selectEmail(filters)
    : undefined;
  const selectedDate = filters.selectedDate;

  const startDate = showWeek
    ? getFirstDateOfWeek(selectedDate, filters.calendarSettings.firstDayOfWeek)
    : startOfDay(selectedDate);
  const endDate = showWeek
    ? getLastDateOfWeek(selectedDate, filters.calendarSettings.firstDayOfWeek)
    : endOfDay(selectedDate);

  return useSWR<OutlookEvent[]>(
    emailIds
      ? `/users/${emailIds.join(",")}/calendarView?startDateTime=${formatToIso(
          startDate
        )}&endDateTime=${formatToIso(endDate)}`
      : null,
    () => {
      const promises =
        emailIds?.map((id) =>
          fetcher.graph.get<{ value?: OutlookEvent[] }>(
            `/users/${id}/calendarView?startDateTime=${formatToIso(
              startDate
            )}&endDateTime=${formatToIso(endDate)}&top=200`
          )
        ) ?? [];

      return Promise.allSettled(promises).then((results) =>
        results
          .flatMap((r) => {
            if (r.status === "fulfilled") {
              return r?.value?.value;
            }
            return undefined;
          })
          .filter(isDefined)
      );
    },
    {
      shouldRetryOnError: false,
      revalidateOnFocus: false,
      refreshInterval: 60 * 1000, // 1 minutes caching
    }
  );
};
